import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Building, Search } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Separator } from "./ui/separator";
import { organigrammeAPI } from "../services/api";
import { useCompanyInfo } from "../hooks/useCompanyInfo";
import OrgTreeHeader from "./organigramme/OrgTreeHeader";
import OrgCompanyRoot from "./organigramme/OrgCompanyRoot";
import OrgDepartementNode from "./organigramme/OrgDepartementNode";

const OrganigrammeTree = ({
  onDepartementSelect,
  selectedDepartementId = null,
  showActions = false,
  compact = false,
  className = "",
}) => {
  const { t } = useTranslation();
  const [departements, setDepartements] = useState([]);
  const [expandedNodes, setExpandedNodes] = useState(new Set([1]));
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showDetails, setShowDetails] = useState(!compact);
  const companyInfo = useCompanyInfo();

  const companyPrimaryColor = companyInfo?.company?.primary_color || "#3B82F6";
  const companyLegalName = companyInfo?.company?.legal_name || companyInfo?.company?.trade_name || "";
  const companyLogoUrl = (() => {
    const url = companyInfo?.company?.logo_url;
    if (!url || typeof url !== "string" || url.trim() === "") return null;
    if (url.startsWith("/uploads/")) {
      return process.env.NODE_ENV === "development"
        ? `${process.env.REACT_APP_API_URL || "http://localhost:8000"}${url}`
        : url;
    }
    return url;
  })();

  useEffect(() => { loadOrganigramme(); }, []);

  const loadOrganigramme = async () => {
    try {
      setLoading(true);
      const response = await organigrammeAPI.getOrganizationChart();
      setDepartements(response.organization_chart);
      setExpandedNodes(new Set([1]));
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const toggleExpand = (departementId) => {
    setExpandedNodes((prev) => {
      const next = new Set(prev);
      next.has(departementId) ? next.delete(departementId) : next.add(departementId);
      return next;
    });
  };

  const handleDepartementClick = (departement) => {
    if (onDepartementSelect) onDepartementSelect(departement);
  };

  const filterDepartements = (deps, term) => {
    if (!term) return deps;
    return deps
      .filter((dept) => {
        const matches = dept.name.toLowerCase().includes(term.toLowerCase()) ||
          dept.code?.toLowerCase().includes(term.toLowerCase());
        const hasMatchingChildren = dept.enfants?.length > 0 &&
          filterDepartements(dept.enfants, term).length > 0;
        return matches || hasMatchingChildren;
      })
      .map((dept) => ({ ...dept, enfants: dept.enfants ? filterDepartements(dept.enfants, term) : [] }));
  };

  const expandAll = () => {
    const getAllIds = (deps) => {
      let ids = [];
      deps.forEach((d) => { ids.push(d.id); if (d.enfants) ids = ids.concat(getAllIds(d.enfants)); });
      return ids;
    };
    setExpandedNodes(new Set(getAllIds(departements)));
  };

  const collapseAll = () => setExpandedNodes(new Set());

  const filteredDepartements = filterDepartements(departements, searchTerm);
  const nodeProps = { expandedNodes, selectedDepartementId, compact, showDetails, toggleExpand, onSelect: handleDepartementClick };

  if (loading) {
    return (
      <Card className={className}>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center space-x-2 text-lg">
            <Building className="h-5 w-5 text-primary" />
            <span>{t("organigramme.title")}</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-40">
            <div className="space-y-3 text-center">
              <div className="animate-spin rounded-full h-10 w-10 border-2 border-primary border-t-transparent mx-auto"></div>
              <p className="text-sm text-muted-foreground">{t("organigramme.loading")}</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={className}>
      <OrgTreeHeader
        showDetails={showDetails}
        setShowDetails={setShowDetails}
        expandAll={expandAll}
        collapseAll={collapseAll}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
      />
      <CardContent className="pt-0">
        <div className="space-y-3">
          <OrgCompanyRoot
            companyLogoUrl={companyLogoUrl}
            companyLegalName={companyLegalName}
            companyPrimaryColor={companyPrimaryColor}
            selectedDepartementId={selectedDepartementId}
            onSelect={handleDepartementClick}
          />
          <Separator className="my-2" />
          <div className="space-y-1 max-h-80 overflow-y-auto pr-2">
            {filteredDepartements.length > 0 ? (
              filteredDepartements.map((departement) => (
                <OrgDepartementNode key={departement.id} departement={departement} level={0} {...nodeProps} />
              ))
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <Search className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">{t("organigramme.noDept")}</p>
                {searchTerm && <p className="text-xs mt-1">{t("organigramme.forTerm", { term: searchTerm })}</p>}
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default OrganigrammeTree;
